import { useState } from 'react';
import {
  Badge,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useEventAgentRuns } from 'src/Modules/PredictionMapper/api/useEventAgent';

interface Props {
  /** Open the drawer on this run. */
  onSelect: (runId: string) => void;
}

const STATUS_COLOR: Record<
  string,
  'default' | 'info' | 'success' | 'error' | 'warning'
> = {
  queued: 'warning',
  running: 'info',
  completed: 'success',
  failed: 'error',
};

const formatRunWhen = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = Date.now();
  const deltaSec = Math.floor((now - d.getTime()) / 1000);
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  return d.toLocaleString();
};

/**
 * Header affordance on the Opportunities page: opens a menu of recent
 * event-agent runs (fix-match + map-event). Click a row to open the
 * `EventAgentRunDrawer` on that run — lets the operator watch any run live
 * without having to dispatch a new one. The badge shows in-flight count and
 * auto-refreshes every 3s via `useEventAgentRuns`.
 */
export const EventAgentRunsButton = ({ onSelect }: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { data, isLoading } = useEventAgentRuns(15);
  const runs = data?.runs ?? [];
  const runningCount = runs.filter(
    (r) => r.status === 'queued' || r.status === 'running',
  ).length;

  return (
    <>
      <Tooltip title="Recent fix-match / map-event runs">
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ color: 'text.primary' }}
        >
          <Badge
            badgeContent={runningCount}
            color="warning"
            max={9}
            overlap="circular"
          >
            <SmartToyIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { width: 360, maxHeight: 440 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">Recent agent runs</Typography>
          <Typography variant="caption" color="text.secondary">
            Click one to watch its progress
          </Typography>
        </Box>
        <Divider />
        {isLoading && <MenuItem disabled>Loading…</MenuItem>}
        {!isLoading && runs.length === 0 && (
          <MenuItem disabled>No runs yet</MenuItem>
        )}
        {runs.map((r) => {
          const title =
            r.kind === 'fix-match'
              ? r.fixMatchSnapshot?.pairName ||
                r.pairId ||
                'fix-match'
              : r.eventQuery;
          return (
            <MenuItem
              key={r._id}
              onClick={() => {
                onSelect(r._id);
                setAnchor(null);
              }}
              sx={{ alignItems: 'flex-start', py: 1 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip
                    size="small"
                    label={r.status}
                    color={STATUS_COLOR[r.status] ?? 'default'}
                    sx={{ height: 18, fontSize: '0.6rem' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {r.kind === 'fix-match' ? 'fix-match' : 'map-event'}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    {formatRunWhen(r.createdAt)}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.78rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {title}
                </Typography>
                {r.kind === 'fix-match' && r.reason && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.65rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    “{r.reason}”
                  </Typography>
                )}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
